import React, { useEffect, useMemo, useState } from "react";
import CustomDataTable from "../../../../../CommonElements/Table/CustomTable";
import TableSearch from "../../../../../CommonElements/HeaderTableSearch";
import {
  approvalAttendanceModal,
  Date,
  Export,
  infoColor,
  NA,
  Name,
  Reason,
  SearchTableButton,
  Status,
} from "../../../../../utils/Constant";
import { TableColumn } from "react-data-table-component";
import { Link } from "react-router-dom";
import { Btn, P } from "../../../../../AbstractElements";
import { dateConversion } from "../../../../../utils/dateUtils";
import BadgeDetails from "../../../../../CommonElements/Badge/BadgeDetails";
import { getStatus } from "../../../../../utils/getStatus";
import IconComponent from "../../../../../CommonElements/IconComponent";
import { ModalType } from "../../../../../Types/UserProfileDetails";
import { IAttendanceRequestData } from "../../types";
import { AppDispatch } from "../../../../../ReduxToolkit/Store";
import { openModal } from "../../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import { useStaggeredRowStyles } from "../../../../../hooks/useStaggeredRowStyle";
import SkeletonTable from "../../../../../CommonElements/Loader/SkeletonTableLoader";
import "../../../../attendance/attendance.scss";

export interface ISelectRows {
  allSelected: boolean;
  selectedCount: number;
  selectedRows: Array<{
    attendance_request_id: number;
    date: string;
    in_time: string;
    out_time: string;
    reason: string;
    status: string;
    team_member: {
      id: number;
      name: string;
      email: string;
      role: number;
      status: string;
    };
  }>;
}

export interface ITeamMembersAttendanceTableProps {
  filterText: string;
  dispatch: AppDispatch;
  toggleCleared: boolean;
  teamMembersAttendanceList: IAttendanceRequestData[];
  teamMemberAttendanceListLoading: boolean;
  setFilterText: (text: string) => void;
  handleOpenModal: (modalType: ModalType, id: number) => void;
  onRowSelected: (state: ISelectRows) => void;
  location: any;
}

const TeamMembersAttendanceTable = ({
  dispatch,
  handleOpenModal,
  filterText,
  onRowSelected,
  setFilterText,
  toggleCleared,
  location,
  teamMembersAttendanceList,
  teamMemberAttendanceListLoading,
}: ITeamMembersAttendanceTableProps) => {
  const [animate, setAnimate] = useState<boolean>(false);

  const handleExport = () =>
    dispatch(
      openModal({ modalType: "exportEmployeeAttendanceCorrectRequest" })
    );

  const tableData = useMemo<
    (IAttendanceRequestData & { _rowClass: string })[]
  >(() => {
    return teamMembersAttendanceList.map((row, i) => ({
      ...row,
      _rowClass: `row-animate row-delay-${i}`,
    }));
  }, [teamMembersAttendanceList]);

  const filterData = useMemo(() => {
    return (
      tableData?.filter(
        (d) =>
          d.team_member.name
            .toLowerCase()
            ?.replaceAll(" ", "")
            .includes(filterText?.toLowerCase()?.replaceAll(" ", "")) ||
          d.team_member.email
            ?.toLowerCase()
            ?.replaceAll(" ", "")
            .includes(filterText?.toLowerCase()?.replaceAll(" ", ""))
      ) || []
    );
  }, [tableData, filterText]);

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div className="d-flex">
        <TableSearch
          value={filterText}
          label={SearchTableButton}
          containerClassName="dataTables_filter d-flex align-items-center"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFilterText(e.target.value)
          }
        />
        {location.pathname.includes("/hr-zone/employee-attendance") && (
          <Btn color={infoColor} className="ms-3" onClick={handleExport}>
            {Export}
          </Btn>
        )}
      </div>
    );
  }, [filterText, tableData]);

  const columns = useMemo<TableColumn<IAttendanceRequestData>[]>(() => {
    const baseCols: TableColumn<IAttendanceRequestData>[] = [
      {
        name: "#",
        cell: (row, index) => index + 1,
        sortable: false,
        width: "60px",
      },
      {
        name: Date,
        selector: (row) => dateConversion(row.date, true),
        sortable: true,
        width: "150px",
      },
      {
        name: "Emp Code",
        cell: (row) => <P className="m-0 f-12">{row.team_member.emp_code}</P>,
        sortable: false,
        width: "100px",
      },

      {
        name: Name,
        cell: (row) => (
          <div className="d-flex align-items-center">
            <IconComponent
              className="fa fa-edit text-primary f-16 pointer me-3"
              onClick={() =>
                handleOpenModal(
                  approvalAttendanceModal,
                  row.attendance_request_id
                )
              }
            />
            <Link
              className=""
              to={`/user-management/user/${row.team_member.id}`}
            >
              <P className="m-0 f-12">{row.team_member.name}</P>
              <P className="m-0 f-12">{row.team_member.email}</P>
            </Link>
          </div>
        ),
        sortable: true,
        width: "250px",
      },
      {
        name: "Managers",
        cell: (row) => <P className="m-0 f-12">{row.managers}</P>,
        sortable: true,
        width: "250px",
      },
      {
        name: "In & Out Time",
        cell: (row) => (
          <div>
            <P className="m-0 f-12">
              {row.in_time ? row.in_time : NA} -{" "}
              {row.out_time ? row.out_time : NA}
            </P>
          </div>
        ),
        sortable: true,
        width: "160px",
      },
      {
        name: Status,
        cell: (row) => (
          <BadgeDetails
            className="text-capitalize f-12"
            color={getStatus(row.status)}
            text={row.status}
          />
        ),
        sortable: true,
        width: "100px",
      },
      // {
      //   name: Action,
      //   cell: (row) => (
      //     <IconComponent
      //       className="fa fa-edit text-primary f-16 pointer"
      //       onClick={() =>
      //         handleOpenModal(
      //           approvalAttendanceModal,
      //           row.attendance_request_id
      //         )
      //       }
      //     />
      //   ),
      //   width: "80px",
      // },
      {
        name: Reason,
        selector: (row) => row.reason,
        sortable: true,
        width: "300px",
      },
    ];
    return baseCols;
  }, [tableData, filterText]);

  useEffect(() => {
    if (
      !teamMemberAttendanceListLoading &&
      teamMembersAttendanceList.length > 0
    ) {
      const timeout = setTimeout(() => setAnimate(true), 50);
      return () => clearTimeout(timeout);
    }
    setAnimate(false); // only reset if data is truly empty
  }, [teamMemberAttendanceListLoading, teamMembersAttendanceList]);

  return (
    <div className="table-responsive user-datatable custom-scrollbar d-none d-lg-block">
      <CustomDataTable
        subHeader
        striped
        selectableRows
        data={filterData || []}
        columns={columns}
        onSelectRowsChange={onRowSelected}
        toggleCleared={toggleCleared}
        subHeaderComponent={subHeaderComponentMemo}
        loading={teamMemberAttendanceListLoading}
        loaderComponent={
          <SkeletonTable
            rows={tableData.length || 10}
            columns={columns}
            rowHeight={40}
            gap={4}
          />
        }
      />
    </div>
  );
};

export default TeamMembersAttendanceTable;
